import { useCallback, useContext, useMemo, useState } from 'react'
import { AppDialog, type AppDialogAction } from '../Common/AppDialog'
import { Button } from '../Common/Button'
import { Card } from '../Common/Card'
import { SettingsLinkChip } from '../Common/SettingsLinkChip'
import { useNls } from '../../nls/nlsProvider'
import type { NlsKey } from '../../nls/strings_nls'
import { useAuth } from '../../context/useAuth'
import { useToast } from '../../context/useToast'
import { useTemplateEngine } from '../../hooks/useTemplateEngine'
import { TourGuideContext } from '../tourguide/tourGuideContextCore'
import { getRegisteredTourIds } from '../tourguide/TourRegistry'
import { matchTemplates } from '../../utils/onboardingTemplateMatch'
import { BUNDLED_EXPLORE_TEMPLATES } from '../../data/explore_templates'
import { MODULE_TITLE_NLS_KEYS } from '../../utils/workspaceModulesSurfaces'
import { MODULE_KEYS } from '../../utils/workspaceModules'
import { isTeamWorkspace } from '../../utils/workspaceKind'
import { WorkspaceContext } from '../../context/workspaceContextCore'
import { roleCanPerform } from '../../utils/workspacePermissions'
import type { WorkspaceRole } from '../../context/WorkspaceContext'
import {
  clearWelcomePending,
  markWelcomeSeen,
  shouldShowWorkspaceWelcome,
} from '../../utils/workspaceWelcome'
import type { Workspace } from '../../types/workspace'
import type { DashboardViewKey } from '../../types/schema'
import type { WorkspaceSettingsSection } from '../Admin/AdminDashboard'

export interface WorkspaceWelcomeProps {
  workspace: Workspace
  open?: boolean
  onClose?: () => void
  onLandOnDefaultView?: (view: DashboardViewKey) => void
  onOpenSettingsSection?: (section: WorkspaceSettingsSection) => void
  onOpenInvites?: () => void
  onOpenHelperInvite?: () => void
  onImportTemplate?: (templateId: string) => Promise<void>
  onStartTour?: (tourId: string) => void
  currentUserUid?: string | null
  isAdmin?: boolean
  role?: WorkspaceRole
}

const DASHBOARD_VIEW_TITLE_NLS: Record<DashboardViewKey, NlsKey> = {
  TIMELINE: 'NLSDashboardViewTimeline',
  CONSTELLATION: 'NLSDashboardViewConstellation',
  RADAR: 'NLSDashboardViewRadar',
  SANDFALL: 'NLSDashboardViewSandfall',
  JOURNEY: 'NLSDashboardViewJourney',
  GRID: 'NLSDashboardViewGrid',
  HEATMAP: 'NLSDashboardViewHeatmap',
  STRIPBOARD: 'NLSDashboardViewStripboard',
  WARROOM: 'NLSDashboardViewWarRoom',
  LEADERBOARD: 'NLSDashboardViewReliability',
  PERIMETER: 'NLSDashboardViewPerimeter',
  LOOTDROP: 'NLSDashboardViewLootDrop',
  FLOWBAND: 'NLSDashboardViewFlowband',
  BURNSHORE: 'NLSDashboardViewBurnshore',
  CADENCE: 'NLSDashboardViewCadence',
}

const HELPER_MODULE_KEYS = ['helperCount', 'helperForms', 'helperScan'] as const
const HELPER_MODULE_TITLES: Record<typeof HELPER_MODULE_KEYS[number], NlsKey> = {
  helperCount: 'NLSWorkspaceModuleHelperCountTitle',
  helperForms: 'NLSWorkspaceModuleHelperFormsTitle',
  helperScan: 'NLSWorkspaceModuleHelperScanTitle',
}

export function WorkspaceWelcome({
  workspace,
  open,
  onClose,
  onLandOnDefaultView,
  onOpenSettingsSection,
  onOpenInvites,
  onOpenHelperInvite,
  onImportTemplate,
  onStartTour,
  currentUserUid,
  isAdmin: isAdminProp,
  role: roleProp,
}: WorkspaceWelcomeProps) {
  const { t } = useNls()
  const { currentUser } = useAuth()
  const { showToast } = useToast()
  const templateEngine = useTemplateEngine()
  const tourGuideContext = useContext(TourGuideContext)
  const workspaceContext = useContext(WorkspaceContext)

  const importTemplate = onImportTemplate ?? templateEngine.importTemplate

  const uid = currentUserUid ?? currentUser?.uid ?? null
  const isTeam = isTeamWorkspace(workspace)
  const effectiveRole = roleProp ?? workspaceContext?.activeRole ?? (uid && workspace.ownerId === uid ? 'OWNER' : undefined)
  const isAdmin = isAdminProp ?? (effectiveRole ? roleCanPerform(effectiveRole, 'ADMIN') : false)

  const [dismissedWorkspaceId, setDismissedWorkspaceId] = useState<string | null>(null)
  const isDismissed = dismissedWorkspaceId === workspace.id
  const isModalOpen = open !== undefined ? open : (!isDismissed && shouldShowWorkspaceWelcome(uid, workspace.id))

  const [addingId, setAddingId] = useState<string | null>(null)
  const [addedIds, setAddedIds] = useState<Set<string>>(() => new Set())

  const handleClose = useCallback(() => {
    setDismissedWorkspaceId(workspace.id)
    if (uid && workspace.id) {
      markWelcomeSeen(uid, workspace.id)
      clearWelcomePending(uid, workspace.id)
    }
    const defaultView = workspace.defaultDashboardView ?? 'TIMELINE'
    onLandOnDefaultView?.(defaultView)
    onClose?.()
  }, [uid, workspace.id, workspace.defaultDashboardView, onLandOnDefaultView, onClose])

  const booleanModules = useMemo(() => {
    if (!workspace.modules) return null
    const result: Partial<Record<string, boolean>> = {}
    for (const [key, value] of Object.entries(workspace.modules)) {
      if (typeof value === 'boolean') {
        result[key] = value
      }
    }
    return result
  }, [workspace.modules])

  const matchedTemplateIds = matchTemplates({
    useCase: workspace.onboardingSetup?.useCase,
    focusAreas: workspace.onboardingSetup?.focusAreas,
    intentText: workspace.onboardingSetup?.intentText,
    modules: booleanModules,
  })

  const registeredTourIds = getRegisteredTourIds()
  const hasWelcomeTour = registeredTourIds.includes('welcome')

  const openingView = workspace.defaultDashboardView ?? 'TIMELINE'
  const openingViewLabel = t(DASHBOARD_VIEW_TITLE_NLS[openingView] ?? 'NLSDashboardViewTimeline')

  const enabledViews = useMemo(() => {
    const list = workspace.enabledDashboardViews && workspace.enabledDashboardViews.length > 0
      ? workspace.enabledDashboardViews
      : [openingView]
    return list.map((viewKey) => t(DASHBOARD_VIEW_TITLE_NLS[viewKey] ?? 'NLSDashboardViewTimeline')).join(', ')
  }, [workspace.enabledDashboardViews, openingView, t])

  const activeModulesLabel = useMemo(() => {
    const labels: string[] = []
    if (workspace.modules) {
      for (const key of MODULE_KEYS) {
        const flagKey = `${key}Enabled` as keyof NonNullable<Workspace['modules']>
        if (workspace.modules[flagKey]) {
          const titleKey = MODULE_TITLE_NLS_KEYS[key]
          if (titleKey) labels.push(t(titleKey))
        }
      }
      for (const key of HELPER_MODULE_KEYS) {
        const flagKey = `${key}Enabled` as keyof NonNullable<Workspace['modules']>
        if (workspace.modules[flagKey]) {
          labels.push(t(HELPER_MODULE_TITLES[key]))
        }
      }
    }
    return labels.length > 0 ? labels.join(', ') : t('NLSAdminWorkspaceProfileEmpty')
  }, [workspace.modules, t])

  const aiSuggestionsOn = workspace.workspacePreferences?.aiSuggestionsEnabled !== false
  const notificationsOn = workspace.workspacePreferences?.notificationsEnabled !== false

  const handleAddTemplate = async (templateId: string) => {
    if (!importTemplate || addedIds.has(templateId) || addingId !== null) return
    setAddingId(templateId)
    const tpl = BUNDLED_EXPLORE_TEMPLATES.find((t) => t.id === templateId)
    try {
      await importTemplate(templateId)
      setAddedIds((prev) => new Set(prev).add(templateId))
      showToast(
        t('NLSWorkspaceWelcomeTemplateImportSuccess', {
          name: tpl?.title ?? templateId,
        }),
        'success',
      )
    } catch {
      showToast(t('NLSWorkspaceWelcomeTemplateImportFailed'), 'error')
    } finally {
      setAddingId(null)
    }
  }

  if (!isModalOpen) {
    return null
  }

  const audience = workspace.onboardingSetup?.audience
  const showInviteSection = isTeam && (audience === 'TEAM' || audience === 'SOLO_HELPERS')

  const actions: AppDialogAction[] = [
    {
      label: t('NLSWorkspaceWelcomeGoToDashboard'),
      variant: 'primary',
      onClick: handleClose,
      testId: 'welcome-go-to-dashboard',
    },
  ]

  return (
    <AppDialog
      open={isModalOpen}
      title={t('NLSWorkspaceWelcomeTitle')}
      message={t('NLSWorkspaceWelcomeSubtitle')}
      size="lg"
      onDismiss={handleClose}
      actions={actions}
      data-testid="workspace-welcome-dialog"
    >
      <div className="flex flex-col gap-4">
        {/* Section 1: Your workspace is set up */}
        <Card
          title={t('NLSWorkspaceWelcomeSetupSummaryTitle')}
          className="workspaceWelcomeSummaryCard"
        >
          <div className="flex flex-col gap-2.5 text-sm">
            <div className="flex items-center justify-between gap-2 border-b border-[var(--border)] pb-2">
              <span className="text-[var(--muted)]">{t('NLSWorkspaceWelcomeOpeningViewLabel')}</span>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-[var(--text-strong)]">{openingViewLabel}</span>
                <SettingsLinkChip
                  label={t('NLSWorkspaceWelcomeChangeInSettings')}
                  onClick={() => {
                    handleClose()
                    onOpenSettingsSection?.('BOARDS')
                  }}
                  testId="welcome-change-boards"
                />
              </div>
            </div>

            <div className="flex items-center justify-between gap-2 border-b border-[var(--border)] pb-2">
              <span className="text-[var(--muted)]">{t('NLSWorkspaceWelcomeEnabledViewsLabel')}</span>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-[var(--text-strong)]">{enabledViews}</span>
                <SettingsLinkChip
                  label={t('NLSWorkspaceWelcomeChangeInSettings')}
                  onClick={() => {
                    handleClose()
                    onOpenSettingsSection?.('BOARDS')
                  }}
                  testId="welcome-change-views"
                />
              </div>
            </div>

            <div className="flex items-center justify-between gap-2 border-b border-[var(--border)] pb-2">
              <span className="text-[var(--muted)]">{t('NLSWorkspaceWelcomeActiveModulesLabel')}</span>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-[var(--text-strong)]">{activeModulesLabel}</span>
                <SettingsLinkChip
                  label={t('NLSWorkspaceWelcomeChangeInSettings')}
                  onClick={() => {
                    handleClose()
                    onOpenSettingsSection?.('MODULES')
                  }}
                  testId="welcome-change-modules"
                />
              </div>
            </div>

            <div className="flex items-center justify-between gap-2 border-b border-[var(--border)] pb-2">
              <span className="text-[var(--muted)]">{t('NLSWorkspaceWelcomeAiSuggestionsLabel')}</span>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-[var(--text-strong)]">
                  {aiSuggestionsOn ? t('NLSWorkspaceWelcomeEnabled') : t('NLSWorkspaceWelcomeDisabled')}
                </span>
                <SettingsLinkChip
                  label={t('NLSWorkspaceWelcomeChangeInSettings')}
                  onClick={() => {
                    handleClose()
                    onOpenSettingsSection?.('OVERVIEW')
                  }}
                  testId="welcome-change-ai"
                />
              </div>
            </div>

            <div className="flex items-center justify-between gap-2">
              <span className="text-[var(--muted)]">{t('NLSWorkspaceWelcomeNotificationsLabel')}</span>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-[var(--text-strong)]">
                  {notificationsOn ? t('NLSWorkspaceWelcomeEnabled') : t('NLSWorkspaceWelcomeDisabled')}
                </span>
                <SettingsLinkChip
                  label={t('NLSWorkspaceWelcomeChangeInSettings')}
                  onClick={() => {
                    handleClose()
                    onOpenSettingsSection?.('NOTIFICATIONS')
                  }}
                  testId="welcome-change-notifications"
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Section 2: Templates (3-6 from matchTemplates; 0 hides the section) */}
        {matchedTemplateIds.length > 0 && (
          <Card
            title={t('NLSWorkspaceWelcomeTemplatesSectionTitle')}
            subtitle={t('NLSWorkspaceWelcomeTemplatesSectionSubtitle')}
            className="workspaceWelcomeTemplatesCard"
          >
            <div className="flex flex-col gap-2.5">
              {matchedTemplateIds.map((templateId) => {
                const tpl = BUNDLED_EXPLORE_TEMPLATES.find((t) => t.id === templateId)
                const isAdded = addedIds.has(templateId)
                const isAdding = addingId === templateId
                return (
                  <div
                    key={templateId}
                    data-testid={`welcome-template-${templateId}`}
                    className="flex items-center justify-between gap-3 rounded-[var(--radius-ctl)] border border-[var(--border)] p-2.5"
                  >
                    <div className="flex flex-col min-w-0">
                      <strong className="text-sm font-semibold text-[var(--text-strong)] truncate">
                        {tpl?.title ?? templateId}
                      </strong>
                      <span className="text-xs text-[var(--muted)] truncate">
                        {tpl?.description || tpl?.category || ''}
                      </span>
                    </div>
                    <Button
                      variant={isAdded ? 'tertiary' : 'secondary'}
                      size="sm"
                      disabled={isAdded || isAdding}
                      loading={isAdding}
                      onClick={() => void handleAddTemplate(templateId)}
                      data-testid={`welcome-template-add-${templateId}`}
                    >
                      {isAdded
                        ? t('NLSWorkspaceWelcomeTemplateAdded')
                        : isAdding
                          ? t('NLSWorkspaceWelcomeTemplateAdding')
                          : t('NLSWorkspaceWelcomeTemplateAdd')}
                    </Button>
                  </div>
                )
              })}
            </div>
          </Card>
        )}

        {/* Section 3: Invite (Team only: audience TEAM -> members invite; SOLO_HELPERS -> helper invite; SOLO and Personal hidden) */}
        {showInviteSection && (
          <Card
            title={t('NLSWorkspaceWelcomeInviteSectionTitle')}
            subtitle={t('NLSWorkspaceWelcomeInviteSectionSubtitle')}
            className="workspaceWelcomeInviteCard"
          >
            <div className="flex items-center gap-2">
              {audience === 'TEAM' && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    handleClose()
                    onOpenInvites?.()
                  }}
                  data-testid="welcome-invite-members-btn"
                >
                  {t('NLSWorkspaceWelcomeInviteMembersButton')}
                </Button>
              )}
              {audience === 'SOLO_HELPERS' && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    handleClose()
                    if (onOpenHelperInvite) {
                      onOpenHelperInvite()
                    } else {
                      onOpenInvites?.()
                    }
                  }}
                  data-testid="welcome-invite-helpers-btn"
                >
                  {t('NLSWorkspaceWelcomeInviteHelpersButton')}
                </Button>
              )}
            </div>
          </Card>
        )}

        {/* Section 4: Take the tour (shown only when registered tour ids includes 'welcome') */}
        {hasWelcomeTour && (
          <div className="flex items-center justify-start">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                handleClose()
                if (onStartTour) {
                  onStartTour('welcome')
                } else if (tourGuideContext?.startTour) {
                  tourGuideContext.startTour('welcome')
                }
              }}
              data-testid="welcome-take-tour-btn"
            >
              {t('NLSWorkspaceWelcomeTakeTourButton')}
            </Button>
          </div>
        )}

        {/* Section 5: More tools in Settings -> Modules (admins only) */}
        {isAdmin && (
          <div className="flex items-center justify-start">
            <Button
              variant="tertiary"
              size="sm"
              onClick={() => {
                handleClose()
                onOpenSettingsSection?.('MODULES')
              }}
              data-testid="welcome-more-modules-btn"
            >
              {t('NLSWorkspaceWelcomeMoreModulesHint')}
            </Button>
          </div>
        )}
      </div>
    </AppDialog>
  )
}
